/** Session-persisted via localStorage (like trash-after-connect). Default: Go to · Older. */

export const AUTO_ADVANCE_STORAGE_KEY = 'nu-email-auto-advance';
export const AUTO_ADVANCE_OFF = 'off';
export const AUTO_ADVANCE_LIST = 'list';
export const AUTO_ADVANCE_OLDER = 'older';
export const AUTO_ADVANCE_NEWER = 'newer';
export const AUTO_ADVANCE_DEFAULT = AUTO_ADVANCE_OLDER;

/** First control: stay on this email vs leave it. */
export const AUTO_ADVANCE_MODE_STAY = 'stay';
export const AUTO_ADVANCE_MODE_GO = 'go';
export const AUTO_ADVANCE_MODE_OPTIONS = [
  { text: 'Go to', value: AUTO_ADVANCE_MODE_GO },
  { text: 'Stay', value: AUTO_ADVANCE_MODE_STAY },
];

/** Second control (when Go to): destination. */
export const AUTO_ADVANCE_GO_TO_OPTIONS = [
  { text: 'Older', value: AUTO_ADVANCE_OLDER },
  { text: 'Newer', value: AUTO_ADVANCE_NEWER },
  { text: 'Message list', value: AUTO_ADVANCE_LIST },
];

const GO_TO_VALID = new Set([AUTO_ADVANCE_OLDER, AUTO_ADVANCE_NEWER, AUTO_ADVANCE_LIST]);

/** Remembers last Older/Newer/List so Stay → Go to restores it. */
export const AUTO_ADVANCE_LAST_GO_TO_STORAGE_KEY = 'nu-email-auto-advance-last-go-to';

const VALID = new Set([
  AUTO_ADVANCE_OFF,
  AUTO_ADVANCE_LIST,
  AUTO_ADVANCE_OLDER,
  AUTO_ADVANCE_NEWER,
]);

export function readAutoAdvancePref() {
  if (typeof window === 'undefined' || !window.localStorage) return AUTO_ADVANCE_DEFAULT;
  const raw = window.localStorage.getItem(AUTO_ADVANCE_STORAGE_KEY);
  if (raw === null) return AUTO_ADVANCE_DEFAULT;
  return VALID.has(raw) ? raw : AUTO_ADVANCE_DEFAULT;
}

export function writeAutoAdvancePref(value) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const next = VALID.has(value) ? value : AUTO_ADVANCE_DEFAULT;
  window.localStorage.setItem(AUTO_ADVANCE_STORAGE_KEY, next);
  if (GO_TO_VALID.has(next)) {
    window.localStorage.setItem(AUTO_ADVANCE_LAST_GO_TO_STORAGE_KEY, next);
  }
}

function readLastGoToPref() {
  if (typeof window === 'undefined' || !window.localStorage) return AUTO_ADVANCE_DEFAULT;
  const raw = window.localStorage.getItem(AUTO_ADVANCE_LAST_GO_TO_STORAGE_KEY);
  if (raw === null) {
    const cur = readAutoAdvancePref();
    return GO_TO_VALID.has(cur) ? cur : AUTO_ADVANCE_DEFAULT;
  }
  return GO_TO_VALID.has(raw) ? raw : AUTO_ADVANCE_DEFAULT;
}

export function autoAdvanceModeFromPref(pref = readAutoAdvancePref()) {
  return pref === AUTO_ADVANCE_OFF ? AUTO_ADVANCE_MODE_STAY : AUTO_ADVANCE_MODE_GO;
}

export function autoAdvanceGoToFromPref(pref = readAutoAdvancePref()) {
  return GO_TO_VALID.has(pref) ? pref : readLastGoToPref();
}

/** Combine Stay/Go-to mode + destination into the stored pref value. */
export function resolveAutoAdvancePref(mode, goTo) {
  if (mode === AUTO_ADVANCE_MODE_STAY) return AUTO_ADVANCE_OFF;
  return GO_TO_VALID.has(goTo) ? goTo : AUTO_ADVANCE_DEFAULT;
}

/** Opt-in: open firm note popup after a full connect (default off). */
export const OPEN_NOTE_AFTER_CONNECT_STORAGE_KEY = 'nu-email-open-note-after-connect';

export function readOpenNoteAfterConnectPref() {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  return window.localStorage.getItem(OPEN_NOTE_AFTER_CONNECT_STORAGE_KEY) === '1';
}

export function writeOpenNoteAfterConnectPref(enabled) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(OPEN_NOTE_AFTER_CONNECT_STORAGE_KEY, enabled ? '1' : '0');
}

/**
 * Newest-first list: index 0 = newer, higher index = older.
 * Prefer direction, else the other; end when neither exists.
 */
export function pickAutoAdvanceNeighbor(items, currentId, preferred) {
  const list = Array.isArray(items) ? items : [];
  const idx = list.findIndex((r) => r && r.id === currentId);
  if (idx < 0) {
    return { end: true };
  }
  const olderIdx = idx + 1;
  const newerIdx = idx - 1;
  const hasOlder = olderIdx < list.length;
  const hasNewer = newerIdx >= 0;
  const wantOlder = preferred !== AUTO_ADVANCE_NEWER;

  if (wantOlder) {
    if (hasOlder) {
      return { item: list[olderIdx], usedFallback: false, openedAs: AUTO_ADVANCE_OLDER };
    }
    if (hasNewer) {
      return {
        item: list[newerIdx],
        usedFallback: true,
        fallbackTo: AUTO_ADVANCE_NEWER,
        openedAs: AUTO_ADVANCE_NEWER,
      };
    }
  } else {
    if (hasNewer) {
      return { item: list[newerIdx], usedFallback: false, openedAs: AUTO_ADVANCE_NEWER };
    }
    if (hasOlder) {
      return {
        item: list[olderIdx],
        usedFallback: true,
        fallbackTo: AUTO_ADVANCE_OLDER,
        openedAs: AUTO_ADVANCE_OLDER,
      };
    }
  }
  return { end: true };
}

/**
 * Manual ‹ › browsing in the viewer. Unlike pickAutoAdvanceNeighbor there is no
 * fallback — at an end the arrow just disables. ‹ = newer (idx-1), › = older (idx+1).
 */
export function browsePosition(items, currentId) {
  const list = Array.isArray(items) ? items : [];
  const idx = list.findIndex((r) => r && r.id === currentId);
  return {
    index: idx, // -1 = not on this page (filtered out / other page)
    count: list.length,
    hasNewer: idx > 0,
    hasOlder: idx >= 0 && idx < list.length - 1,
  };
}

export function pickBrowseNeighbor(items, currentId, direction) {
  const list = Array.isArray(items) ? items : [];
  const idx = list.findIndex((r) => r && r.id === currentId);
  if (idx < 0) return null;
  const item = direction === AUTO_ADVANCE_NEWER ? list[idx - 1] : list[idx + 1];
  return item || null;
}

export function autoAdvanceOpenedNotice(openedAs) {
  if (openedAs === AUTO_ADVANCE_OLDER) return 'Showing next older email.';
  if (openedAs === AUTO_ADVANCE_NEWER) return 'Showing next newer email.';
  return '';
}

export function autoAdvanceFallbackNotice(fallbackTo) {
  if (fallbackTo === AUTO_ADVANCE_OLDER) {
    return 'No newer left on this page — showing next older email.';
  }
  if (fallbackTo === AUTO_ADVANCE_NEWER) {
    return 'No older left on this page — showing next newer email.';
  }
  return '';
}

export const AUTO_ADVANCE_END_NOTICE =
  'No more emails left to open from this page.';

/** Snack fragment when advancing (or ending the page). */
export function autoAdvanceNotice(pick) {
  if (!pick || pick.end) return AUTO_ADVANCE_END_NOTICE;
  if (pick.usedFallback) return autoAdvanceFallbackNotice(pick.fallbackTo);
  return autoAdvanceOpenedNotice(pick.openedAs);
}

/**
 * Sticky lead after Older/Newer advance (persistent; snack stays outcome-only).
 * Empty when the open wasn't an advance (manual row click / ‹ › browse) so the
 * sticky doesn't claim a next email; context lives on the button below
 * (lastConnectStickyActionLabel).
 */
export function lastConnectStickyExplain({ openedAs, usedFallback } = {}) {
  if (usedFallback) {
    const t = autoAdvanceFallbackNotice(openedAs);
    if (t) return t;
  }
  return autoAdvanceOpenedNotice(openedAs);
}

export function lastConnectStickyActionLabel(fid) {
  if (fid != null && fid !== '') return `Open note in firm for the email you just connected · FID ${fid}`;
  return 'Open note in firm for the email you just connected';
}

export function lastConnectPageEndExplain(fid) {
  const note = (fid != null && fid !== '')
    ? `You can still open the note in firm for the last email you connected (FID ${fid}).`
    : 'You can still open the note in firm for the last email you connected.';
  return `No more emails left to open from this page. ${note} To find other mail, use the message list.`;
}

/** Popup trash icon tooltip — reflects current After trash or successful connect pref. */
export function trashWithoutConnectTooltip(pref = readAutoAdvancePref()) {
  if (pref === AUTO_ADVANCE_LIST) {
    return 'Move to trash without connecting, then return to the message list';
  }
  if (pref === AUTO_ADVANCE_OFF) {
    return 'Move to trash without connecting (stay on this email)';
  }
  if (pref === AUTO_ADVANCE_NEWER) {
    return 'Move to trash without connecting, then advance to the next newer email';
  }
  return 'Move to trash without connecting, then advance to the next older email';
}

function normalizeConnectOutcomeOpts(opts) {
  if (typeof opts === 'string') {
    return {
      trashAfter: true,
      openNote: false,
      autoAdvance: VALID.has(opts) ? opts : readAutoAdvancePref(),
    };
  }
  const o = opts && typeof opts === 'object' ? opts : {};
  const autoAdvance = VALID.has(o.autoAdvance) ? o.autoAdvance : readAutoAdvancePref();
  return {
    trashAfter: o.trashAfter !== false,
    openNote: !!o.openNote,
    autoAdvance,
  };
}

/** Short Connect outcome caption — e.g. "Move to trash · Open note in firm · Older". */
export function connectOutcomeCaption(opts) {
  const { trashAfter, openNote, autoAdvance } = normalizeConnectOutcomeOpts(opts);
  const bits = [trashAfter ? 'Move to trash' : 'Don’t trash'];
  if (openNote) bits.push('Open note in firm');
  if (autoAdvance === AUTO_ADVANCE_LIST) bits.push('Message list');
  else if (autoAdvance === AUTO_ADVANCE_OFF) bits.push('Stay');
  else if (autoAdvance === AUTO_ADVANCE_NEWER) bits.push('Newer');
  else bits.push('Older');
  return bits.join(' · ');
}

/**
 * Popup Connect tooltip — caption of what this click will do (prefs edited via ▾ / go-to).
 * Same shape as connectOutcomeCaption; no browser-only note (that lives on the controls).
 */
export function connectWithAdvanceTooltip(opts) {
  return connectOutcomeCaption(
    opts === undefined ? { autoAdvance: readAutoAdvancePref() } : opts,
  );
}
